import { useCallback } from 'react';

import { type SignMessagePayload } from '@aptos-labs/wallet-adapter-core';
import { useConnect, useSignMessage } from 'wagmi';
import { injected } from 'wagmi/connectors';

import { useToastStore } from '@pkg/components/Toast/useToastStore';
import { useWalletMutations } from '@pkg/hooks/wallets';
import { usePetraWallet } from '@pkg/hooks/wallets/usePetraWallet';
import { walletConnectConnector } from '@pkg/providers';
import { type SupportedWalletType } from '@pkg/types';
import { getErrorMessage } from '@pkg/utils';

import { getWalletWithPrefix } from '@app/helpers';
import { useWalletStore } from '@app/store/zustand';

interface Connection {
    accounts: readonly string[];
    chainId?: number;
    publicKey?: string;
}

export function useConnectAnyWallet() {
    const { addToast } = useToastStore();
    const { connectAsync } = useConnect();
    const setConnecting = useWalletStore.getState().setConnecting;
    const { verifyWallet, addWallet } = useWalletMutations();
    const { signMessageAsync } = useSignMessage();
    const { handlePetraConnect, signMessage: signPetraMessage } = usePetraWallet();

    const handleConnection = useCallback(
        async (type: SupportedWalletType) => {
            setConnecting({ type, step: 'awaiting-external-action' });
            const isPetraConnect = type === 'petra';
            try {
                let connection: Connection = { accounts: [] };
                if (isPetraConnect) {
                    const petraAccount = await handlePetraConnect();
                    if (petraAccount) {
                        connection = {
                            accounts: [petraAccount.address],
                            publicKey: petraAccount.publicKey as string,
                        };
                    }
                } else {
                    const connector = type === 'metaMask' ? injected({ target: 'metaMask' }) : walletConnectConnector;
                    connection = await connectAsync({ connector });
                }

                if (connection?.accounts?.length) {
                    const accounts = connection.accounts;
                    const publicKey = connection.publicKey || '';
                    const walletAddress = getWalletWithPrefix(accounts[0], connection.chainId);
                    const address = accounts?.[0];
                    const addWalletRes = await addWallet({
                        variables: { accountPublicKey: address, accountAddress: walletAddress },
                    });

                    if (addWalletRes?.accountAdd) {
                        let signature;
                        if (isPetraConnect) {
                            signature = await signPetraMessage(
                                addWalletRes.accountAdd?.messageToSign as SignMessagePayload,
                            );
                        } else {
                            signature = await signMessageAsync({
                                message: addWalletRes.accountAdd?.messageToSign.message,
                            });
                        }
                        const publicKeyPayload = isPetraConnect ? publicKey : address;
                        const signaturePayload = isPetraConnect ? { petra: signature } : { eip712: { signature } };
                        const isVerified = await verifyWallet({
                            variables: {
                                address: walletAddress,
                                publicKey: publicKeyPayload,
                                signature: signaturePayload,
                            },
                        });

                        if (isVerified) {
                            setConnecting({ type, step: 'success' });
                        }
                    }
                }
            } catch (e) {
                addToast({ message: getErrorMessage(e), minimal: true });
                setConnecting({ type, step: 'error' });
            } finally {
                setConnecting(undefined);
            }
        },
        [
            addToast,
            addWallet,
            connectAsync,
            handlePetraConnect,
            setConnecting,
            signMessageAsync,
            signPetraMessage,
            verifyWallet,
        ],
    );

    return {
        connect: handleConnection,
    };
}
